<template>
  <div class="min-h-screen bg-white text-gray-800">
    <!-- Skip Links for Accessibility -->
    <div class="sr-only-focusable">
      <a 
        href="#main-content" 
        class="skip-link bg-blue-600 text-white px-4 py-2 rounded-md font-medium focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        @click="skipToMainContent"
      >
        Skip to main content
      </a>
      <a 
        v-if="showTableOfContents && tocItems.length > 0"
        href="#table-of-contents" 
        class="skip-link bg-blue-600 text-white px-4 py-2 rounded-md font-medium focus:not-sr-only focus:absolute focus:top-4 focus:left-32 focus:z-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ml-2"
        @click="skipToTableOfContents"
      >
        Skip to table of contents
      </a>
    </div>

    <!-- Breadcrumb Navigation -->
    <nav class="bg-gray-50 border-b border-gray-200 py-4" aria-label="Breadcrumb navigation">
      <ol class="flex items-center max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 list-none" role="list">
        <li class="flex items-center">
          <router-link 
            to="/" 
            class="text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:rounded-sm"
            aria-label="Navigate to home page"
          >
            Home
          </router-link>
        </li>
        <li class="flex items-center text-gray-500 font-medium ml-2" aria-current="page">
          <span class="mr-2" aria-hidden="true">/</span>
          <span class="text-gray-800">{{ title }}</span>
        </li>
      </ol>
    </nav>

    <div class="flex max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12 gap-6 lg:gap-8">
      <!-- Table of Contents Sidebar -->
      <aside 
        v-if="showTableOfContents && tocItems.length > 0" 
        id="table-of-contents"
        class="w-full lg:w-80 xl:w-96 bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 h-fit sticky top-6 order-first lg:order-none"
        :class="{ 'lg:block': !isTocCollapsed, 'lg:block': isTocCollapsed }"
        role="complementary"
        aria-labelledby="toc-heading"
      >
        <div class="flex justify-between items-center mb-4">
          <h3 id="toc-heading" class="text-lg font-semibold text-gray-800">Table of Contents</h3>
          <button 
            class="lg:hidden p-2 rounded-md hover:bg-gray-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px] min-w-[44px]"
            @click="toggleToc"
            :aria-expanded="!isTocCollapsed"
            aria-controls="toc-list"
            :aria-label="`${isTocCollapsed ? 'Show' : 'Hide'} table of contents`"
            type="button"
          >
            <span class="sr-only">{{ isTocCollapsed ? 'Show' : 'Hide' }} table of contents</span>
            <svg 
              class="w-5 h-5 transition-transform duration-200" 
              :class="{ 'rotate-180': !isTocCollapsed }"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        <nav 
          id="toc-list"
          class="transition-all duration-300 ease-in-out"
          :class="{ 'hidden lg:block': isTocCollapsed, 'block': !isTocCollapsed }"
          aria-label="Table of contents navigation"
          role="navigation"
        >
          <ul class="space-y-1" role="list">
            <li 
              v-for="(item, index) in tocItems" 
              :key="item.id"
              :class="{
                'ml-0': item.level === 1,
                'ml-4': item.level === 2,
                'ml-8': item.level === 3
              }"
              role="listitem"
            >
              <a 
                :href="`#${item.id}`"
                class="block text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-md text-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px] flex items-center"
                :class="{
                  'font-medium': item.level === 1,
                  'text-xs': item.level === 3
                }"
                @click="scrollToSection(item.id, $event)"
                @keydown="handleTocKeydown($event, index)"
                :aria-label="`Navigate to section: ${item.text}`"
                role="link"
              >
                {{ item.text }}
              </a>
            </li>
          </ul>
        </nav>
      </aside>

      <!-- Main Content -->
      <main 
        id="main-content"
        class="flex-1 min-w-0" 
        :class="{ 'max-w-4xl mx-auto': !showTableOfContents || tocItems.length === 0 }"
        role="main"
        :aria-labelledby="'page-title'"
        tabindex="-1"
      >
        <header class="mb-8 pb-6 border-b-2 border-gray-200">
          <h1 
            id="page-title"
            class="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-2 leading-tight"
            tabindex="-1"
          >
            {{ title }}
          </h1>
          <p class="text-gray-500 text-sm italic" v-if="lastUpdated">
            Last updated: <time :datetime="lastUpdated">{{ formatDate(lastUpdated) }}</time>
          </p>
        </header>

        <div 
          class="prose prose-gray max-w-none text-gray-700 leading-relaxed" 
          ref="contentRef"
          role="document"
        >
          <slot></slot>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import type { TocItem } from '@/types/legal'
import { addBreadcrumbStructuredData, removeBreadcrumbStructuredData } from '@/utils/sitemapUtils'

interface Props {
  title: string
  lastUpdated?: string
  showTableOfContents?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showTableOfContents: true
})

const contentRef = ref<HTMLElement>()
const tocItems = ref<TocItem[]>([])
const isTocCollapsed = ref(true) // Start collapsed on mobile

const generateTableOfContents = () => {
  if (!contentRef.value || !props.showTableOfContents) return

  const headings = contentRef.value.querySelectorAll('h2, h3, h4')
  const items: TocItem[] = []

  headings.forEach((heading, index) => {
    const level = parseInt(heading.tagName.charAt(1)) - 1 // h2=1, h3=2, h4=3
    let id = heading.id

    // Generate ID if not present
    if (!id) {
      id = `section-${index + 1}`
      heading.id = id
    }

    items.push({
      id,
      text: heading.textContent || '',
      level
    })
  })

  tocItems.value = items
}

const scrollToSection = (id: string, event: Event) => {
  event.preventDefault()
  const element = document.getElementById(id)
  if (element) {
    element.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    })
    // Close TOC on mobile after navigation
    if (window.innerWidth < 768) {
      isTocCollapsed.value = true
    }
  }
}

const toggleToc = () => {
  isTocCollapsed.value = !isTocCollapsed.value
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// Accessibility functions for skip links
const skipToMainContent = (event: Event) => {
  event.preventDefault()
  const mainContent = document.getElementById('main-content')
  if (mainContent) {
    mainContent.focus()
    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const skipToTableOfContents = (event: Event) => {
  event.preventDefault()
  const toc = document.getElementById('table-of-contents')
  if (toc) {
    toc.focus()
    toc.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

// Keyboard navigation for table of contents
const handleTocKeydown = (event: KeyboardEvent, currentIndex: number) => {
  const tocLinks = document.querySelectorAll('#toc-list a')
  
  switch (event.key) {
    case 'ArrowDown': {
      event.preventDefault()
      const nextIndex = currentIndex + 1 < tocLinks.length ? currentIndex + 1 : 0
      ;(tocLinks[nextIndex] as HTMLElement).focus()
      break
    }
      
    case 'ArrowUp': {
      event.preventDefault()
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : tocLinks.length - 1
      ;(tocLinks[prevIndex] as HTMLElement).focus()
      break
    }
      
    case 'Home':
      event.preventDefault()
      ;(tocLinks[0] as HTMLElement).focus()
      break
      
    case 'End':
      event.preventDefault()
      ;(tocLinks[tocLinks.length - 1] as HTMLElement).focus()
      break
      
    case 'Enter':
    case ' ':
      event.preventDefault()
      ;(tocLinks[currentIndex] as HTMLElement).click()
      break
  }
}

// Handle window resize
const handleResize = () => {
  if (window.innerWidth >= 768) {
    isTocCollapsed.value = false
  } else {
    isTocCollapsed.value = true
  }
}

onMounted(async () => {
  await nextTick()
  generateTableOfContents()
  
  // Set initial TOC state based on screen size
  handleResize()
  window.addEventListener('resize', handleResize)
  
  // Add breadcrumb structured data
  const breadcrumbs = [
    { name: 'Home', url: window.location.origin },
    { name: props.title, url: window.location.href }
  ]
  addBreadcrumbStructuredData(breadcrumbs)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  removeBreadcrumbStructuredData()
})
</script>

<style scoped>
/* Screen reader only text */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Skip links - hidden until focused */
.sr-only-focusable {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1000;
}

.skip-link {
  position: absolute;
  left: -9999px;
  top: -9999px;
  z-index: 999;
}

.skip-link:focus {
  position: fixed !important;
  left: 1rem !important;
  top: 1rem !important;
  z-index: 1000 !important;
  display: block !important;
}

/* Focus visible for better keyboard navigation */
.focus-visible:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Ensure minimum touch target size for mobile accessibility */
@media (max-width: 768px) {
  button, a {
    min-height: 44px;
    min-width: 44px;
  }
}

/* Print Styles */
@media print {
  nav {
    display: none;
  }
  
  aside {
    display: none;
  }
  
  main {
    max-width: none !important;
  }
  
  h1 {
    font-size: 24pt;
    margin-bottom: 12pt;
    page-break-after: avoid;
  }
  
  .prose :deep(h2) {
    font-size: 16pt;
    margin: 18pt 0 6pt 0;
    page-break-after: avoid;
  }
  
  .prose :deep(h3) {
    font-size: 14pt;
    margin: 12pt 0 4pt 0;
    page-break-after: avoid;
  }
  
  .prose :deep(h4) {
    font-size: 12pt;
    margin: 10pt 0 3pt 0;
    page-break-after: avoid;
  }
  
  .prose :deep(p) {
    margin: 6pt 0;
    orphans: 3;
    widows: 3;
  }
  
  .prose :deep(ul),
  .prose :deep(ol) {
    margin: 6pt 0;
  }
  
  .prose :deep(li) {
    margin: 3pt 0;
  }
  
  .prose :deep(a) {
    color: black;
    text-decoration: underline;
  }
  
  /* Ensure proper page breaks */
  .prose :deep(h2),
  .prose :deep(h3),
  .prose :deep(h4) {
    break-inside: avoid;
  }
  
  .prose :deep(p),
  .prose :deep(li) {
    break-inside: avoid;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  nav {
    background-color: #f0f0f0 !important;
    border-bottom-color: #000000 !important;
  }
  
  aside {
    background-color: #f0f0f0 !important;
    border-color: #000000 !important;
  }
  
  a {
    color: #0000ee !important;
  }
  
  a:hover {
    color: #0000aa !important;
  }
  
  button {
    background-color: #ffffff !important;
    border: 2px solid #000000 !important;
    color: #000000 !important;
  }
  
  button:hover {
    background-color: #000000 !important;
    color: #ffffff !important;
  }
}

/* Enhanced focus styles for better visibility */
a:focus-visible,
button:focus-visible {
  outline: 3px solid #3b82f6;
  outline-offset: 2px;
  border-radius: 4px;
}

/* Ensure sufficient color contrast for all text */
.text-gray-600 {
  color: #4b5563; /* Darker gray for better contrast */
}

.text-gray-500 {
  color: #6b7280; /* Adjusted for better contrast */
}

/* Skip link animations */
.skip-link {
  transition: all 0.3s ease;
}

.skip-link:focus {
  animation: skipLinkFadeIn 0.3s ease-in-out;
}

@keyframes skipLinkFadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  * {
    transition: none !important;
    animation: none !important;
  }
  
  html {
    scroll-behavior: auto !important;
  }
}
</style>